/**
 * Phase 21.4 Batch 3C — 🏅 Admin · Badge Catalog CRUD
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["contest", "artist", "producer", "referral", "crew", "spotlight", "system", "milestone"];
// Backend enum (from /app/backend/badge_catalog_routes.py::VALID_PLACEMENTS):
//   null | "gold" | "silver" | "bronze"
// We keep the same values — only the display labels are more explicit
// so the mobile <select> doesn't collapse the empty-string option.
const PLACEMENTS = [
  { value: "", label: "— none —" },
  { value: "gold", label: "🥇 Gold" },
  { value: "silver", label: "🥈 Silver" },
  { value: "bronze", label: "🥉 Bronze" },
];

const emptyBadge = () => ({
  badge_id: "",
  label: "",
  description: "",
  icon_url: "",
  category: "spotlight",   // 21.4 Batch 3F: default to non-contest so historical
                           // (In The Booth, Weekly Spotlight, Beat of the Week …)
                           // don't hit the "contest_id required" backend guard.
  contest_id: "",
  placement: "",
  active: true,
});

const AdminBadgeCatalogPage = () => {
  const [rows, setRows] = useState([]);
  const [contests, setContests] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | emptyBadge() | existingRow
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        api.get("/admin/badges"),
        api.get("/admin/contests"),
      ]);
      setRows(b.data?.badges || []);
      setContests(c.data?.contests || []);
    } catch (e) {
      toast.error("Failed to load badge catalog");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((b) =>
      (b.badge_id || "").toLowerCase().includes(query) ||
      (b.label || "").toLowerCase().includes(query) ||
      (b.category || "").toLowerCase().includes(query),
    );
  }, [rows, q]);

  const save = async () => {
    if (!editing) return;
    if (!editing.badge_id || !editing.label) {
      toast.error("badge_id and label required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        badge_id: editing.badge_id,
        label: editing.label,
        description: editing.description || "",
        icon_url: editing.icon_url || null,
        category: editing.category,
        contest_id: editing.contest_id || null,
        placement: editing.placement || null,
        active: editing.active !== false,
      };
      await api.post("/admin/badges", payload);
      toast.success("Badge saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (badge) => {
    try {
      await api.patch(`/admin/badges/${badge.badge_id}`, { active: !badge.active });
      toast.success(`${!badge.active ? "Activated" : "Deactivated"} ${badge.badge_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const del = async (badge) => {
    if (!window.confirm(`Delete badge "${badge.badge_id}"? This is only allowed if no user wears it.`)) return;
    try {
      await api.delete(`/admin/badges/${badge.badge_id}`);
      toast.success("Badge deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete blocked (users may wear this badge — deactivate instead)");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-badge-catalog-page">
      <SharedNavigation />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-3xl font-black">🏅 Badge Catalog</h1>
            <p className="text-sm text-gray-400">Central registry — legacy + custom + contest badges.</p>
          </div>
          <Button
            onClick={() => setEditing(emptyBadge())}
            className="bg-white text-black hover:bg-amber-200 font-black"
            data-testid="admin-badge-new-btn"
          >+ New badge</Button>
        </div>

        <div className="flex gap-2 mb-4">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search badges…"
            className="max-w-xs bg-white/5 border-white/10 text-white"
            data-testid="admin-badge-search"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-badge-table">
            <thead className="text-left text-gray-400 text-xs uppercase tracking-wider border-b border-white/10">
              <tr>
                <th className="py-2 pr-3">Badge ID</th>
                <th className="py-2 pr-3">Label</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Placement</th>
                <th className="py-2 pr-3">Contest</th>
                <th className="py-2 pr-3">Active</th>
                <th className="py-2 pr-3">Seeded</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.badge_id} className="border-b border-white/5" data-testid={`admin-badge-row-${b.badge_id}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{b.badge_id}</td>
                  <td className="py-2 pr-3 text-white">{b.label}</td>
                  <td className="py-2 pr-3 text-gray-300">{b.category}</td>
                  <td className="py-2 pr-3 text-gray-300">{b.placement || "—"}</td>
                  <td className="py-2 pr-3 text-[10px] font-mono text-gray-500">{b.contest_id || "—"}</td>
                  <td className="py-2 pr-3">
                    <button
                      onClick={() => toggleActive(b)}
                      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase font-black ${b.active ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-gray-400"}`}
                      data-testid={`admin-badge-toggle-active-${b.badge_id}`}
                    >
                      {b.active ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td className="py-2 pr-3 text-gray-400">{b.seeded ? "yes" : "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditing(b)}
                        className="text-xs text-purple-300 hover:underline"
                        data-testid={`admin-badge-edit-${b.badge_id}`}
                      >Edit</button>
                      <button
                        onClick={() => del(b)}
                        className="text-xs text-rose-300 hover:underline"
                        data-testid={`admin-badge-delete-${b.badge_id}`}
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-gray-500 text-sm">No badges match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="admin-badge-editor-modal">
          <div className="bg-[#12121b] border border-white/10 rounded-xl p-5 max-w-lg w-full mx-4 space-y-3">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-black">{editing.seeded ? "Edit seeded badge" : editing.badge_id ? "Edit badge" : "New badge"}</h2>
              <button
                onClick={() => setEditing(null)}
                className="text-gray-400 hover:text-white"
                data-testid="admin-badge-editor-close"
              >✕</button>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-400">Badge ID</Label>
              <Input
                value={editing.badge_id}
                onChange={(e) => setEditing({ ...editing, badge_id: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "_") })}
                disabled={!!editing.created_at}
                className="bg-white/5 border-white/10 text-white font-mono"
                data-testid="admin-badge-editor-id"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-400">Label</Label>
              <Input
                value={editing.label}
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-badge-editor-label"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-400">Description</Label>
              <Textarea
                value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-badge-editor-description"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-gray-400">Icon URL (optional)</Label>
              <Input
                value={editing.icon_url || ""}
                onChange={(e) => setEditing({ ...editing, icon_url: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-badge-editor-icon"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-gray-400">Category</Label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                  data-testid="admin-badge-editor-category"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#13131a]">{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px] uppercase text-gray-400">Placement</Label>
                <select
                  value={editing.placement || ""}
                  onChange={(e) => setEditing({ ...editing, placement: e.target.value })}
                  className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                  data-testid="admin-badge-editor-placement"
                >
                  {PLACEMENTS.map((p) => (
                    <option
                      key={p.value || "none"}
                      value={p.value}
                      className="bg-[#13131a]"
                      data-testid={`admin-badge-editor-placement-option-${p.value || "none"}`}
                    >
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {editing.category === "contest" && (
              <div>
                <Label className="text-[11px] uppercase text-gray-400">Contest</Label>
                <select
                  value={editing.contest_id || ""}
                  onChange={(e) => setEditing({ ...editing, contest_id: e.target.value })}
                  className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                  data-testid="admin-badge-editor-contest"
                >
                  <option value="" className="bg-[#13131a]">— choose contest —</option>
                  {contests.map((c) => <option key={c.contest_id} value={c.contest_id} className="bg-[#13131a]">{c.title}</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                id="active-cb"
                type="checkbox"
                checked={editing.active !== false}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                data-testid="admin-badge-editor-active"
              />
              <label htmlFor="active-cb" className="text-sm text-white">Active</label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="border-white/20 text-white">Cancel</Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-white text-black font-black hover:bg-amber-200"
                data-testid="admin-badge-editor-save"
              >{saving ? "Saving…" : "Save"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBadgeCatalogPage;
